import { useEffect } from 'react';

/**
 * useSEO — Updates document meta tags for SEO and social sharing.
 * Works for Google (which executes JS) and updates page title.
 * For Facebook/Twitter bots, the /api/og/ endpoints serve static HTML.
 * 
 * Usage in any page component:
 *   useSEO({ title: 'Page Title', description: '...', image: 'https://...' });
 * 
 * To add SEO to new pages:
 *   1. Import: import { useSEO } from '../utils/seo';
 *   2. Call in component: useSEO({ title, description, image, url });
 *   3. Add /api/og/ endpoint in backend/seo_service.py for bot crawlers
 */
export const useSEO = ({ title, description, image, url, type = 'website' }) => {
  useEffect(() => {
    const siteName = 'Anyone Music Records';
    const fullTitle = title ? `${title} | ${siteName}` : siteName;
    const defaultDesc = 'The global stage for passion, creativity, and high-energy EDM';
    const defaultImage = 'https://anyonemusicrecords.com/amr-logo.png';

    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const setMeta = (property, content, isName = false) => {
      const attr = isName ? 'name' : 'property';
      let tag = document.querySelector(`meta[${attr}="${property}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attr, property);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content || '');
    };

    setMeta('description', description || defaultDesc, true);
    setMeta('og:title', fullTitle);
    setMeta('og:description', description || defaultDesc);
    setMeta('og:image', image || defaultImage);
    setMeta('og:url', url || window.location.href);
    setMeta('og:type', type);
    setMeta('og:site_name', siteName);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description || defaultDesc);
    setMeta('twitter:image', image || defaultImage);
    setMeta('twitter:card', 'summary_large_image');

    // Cleanup: restore defaults on unmount
    return () => {
      document.title = siteName;
    };
  }, [title, description, image, url, type]);
};
